import React, { useEffect, useState } from 'react';
import { cx } from '@/components/growth-desk/ui';

/**
 * System states (system board): say what happened, offer one way out. A big word (404 in orange,
 * "Oops." in ink), a title, one line, and the actions.
 */
export const SystemCard: React.FC<{
  big?: React.ReactNode; bigTone?: 'brand' | 'ink'; eyebrow?: string; title: React.ReactNode; body?: React.ReactNode;
  actions?: React.ReactNode; details?: string; dark?: boolean; className?: string;
}> = ({ big, bigTone = 'ink', eyebrow, title, body, actions, details, dark, className }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={cx('flex w-full max-w-[440px] flex-col gap-3 rounded-panel p-7', dark ? 'bg-navy text-on-navy' : 'bg-card text-ink', className)}>
      {big && <div className={cx('font-display text-[72px] font-extrabold leading-none tracking-[-0.05em]', bigTone === 'brand' ? 'text-brand' : 'text-ink')}>{big}</div>}
      {eyebrow && <div className={cx('text-label uppercase', dark ? 'text-on-navy-brand' : 'text-ink-3')}>{eyebrow}</div>}
      <h1 className="m-0 font-display text-[26px] font-extrabold leading-tight tracking-[-0.02em]">{title}</h1>
      {body && <p className={cx('m-0 text-[15px] leading-relaxed', dark ? 'text-on-navy-2' : 'text-ink-2')}>{body}</p>}
      {details && (
        <div className="flex flex-col gap-2">
          <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} className="self-start text-small font-semibold text-ink-3 underline underline-offset-2">
            {open ? 'Hide details' : 'Details'}
          </button>
          {open && <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap rounded-[12px] bg-inset p-3 font-money text-xs text-ink-2">{details}</pre>}
        </div>
      )}
      {actions && <div className="mt-3 flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
};

/** Centers a SystemCard on the page. */
export const SystemPage: React.FC<{ children: React.ReactNode; fullScreen?: boolean }> = ({ children, fullScreen }) => (
  <div className={cx('grid place-items-center bg-ground px-5 py-12', fullScreen ? 'min-h-[100dvh]' : 'min-h-full')}>{children}</div>
);

const btn = 'inline-flex min-h-[44px] items-center rounded-btn px-4 font-display text-sm font-bold no-underline transition-colors';
export const systemButton = {
  navy: `${btn} bg-navy text-on-navy hover:bg-navy-3`,
  ghost: `${btn} bg-transparent text-ink shadow-ring-line hover:shadow-ring-ink`,
  brand: `${btn} bg-brand text-brand-on hover:bg-brand-hover`,
};

/** "You're offline" strip for the top of any page. */
export const OfflineBanner: React.FC = () => {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener('online', on); window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return (
    <div role="status" className="flex items-center gap-2 bg-warn-soft px-5 py-2.5 text-sm font-semibold text-warn md:px-12">
      <span aria-hidden="true">!</span> You’re offline. Anything you change now may not save until you’re back online.
    </div>
  );
};
